<template>
	<AppLayout :title="trans('events.requests.title')">
		<template #actions>
			<PrimaryButton :color="Color.Green" @click.stop="create">
				<i class="fa-solid fa-check mr-2"/>
				{{ trans('general.confirm') }}
			</PrimaryButton>
		</template>
		<template v-if="periods.length > 0 && !hasPendingRequests">
			<LabeledObject required>
				<template #label>{{ trans('events.type') }}</template>
				<select v-model="newRequest.type">
					<option value="generic">{{ trans('events.generic.title') }}</option>
					<option value="joseii">{{ trans('events.joseii.title') }}</option>
				</select>
			</LabeledObject>
			<LabeledObject v-if="newRequest.type === 'generic'" required>
				<template #label>{{ trans('events.requests.name') }}</template>
				<input v-model="newRequest.name" type="text" maxlength="255">
			</LabeledObject>
			<LabeledObject v-if="newRequest.type === 'joseii'" required>
				<template #label>{{ trans('events.requests.lemma') }}</template>
				<input v-model="newRequest.lemma" type="text" maxlength="255">
			</LabeledObject>
			<div>
				<SearchLocation v-model:location-id="newRequest.location_id" :location="newRequest.location" :label="trans('events.requests.location')" local-only/>
			</div>
			<SelectColleges :colleges="newRequest.colleges"/>
			<LabeledObject required>
				<template #label>{{ trans('events.requests.email') }}</template>
				<input v-model="newRequest.email" type="email" maxlength="255">
			</LabeledObject>
			<LabeledObject>
				<template #label>{{ trans('events.requests.available_dates') }}</template>
				<div class="flex gap-3">
					<div v-for="period in periods" class="px-2 py-1 rounded bg-green-100 text-green-800 dark:bg-green-800 dark:text-green-100">{{ dayjs(period.begins_on).format(trans('general.dayjs.medium_date')) }} - {{ dayjs(period.ends_on).format(trans('general.dayjs.medium_date')) }}</div>
				</div>
			</LabeledObject>
			<div class="flex flex-col lg:flex-row lg:items-end gap-3">
				<LabeledObject full required>
					<template #label>{{ trans('events.requests.begins_on') }}</template>
					<input v-model="newRequest.begins_on" type="date">
				</LabeledObject>
				<LabeledObject full required>
					<template #label>{{ trans('events.requests.ends_on') }}</template>
					<input v-model="newRequest.ends_on" type="date">
				</LabeledObject>
				<LabeledObject required>
					<template #label>{{ trans('events.requests.estimated_participants') }}</template>
					<input v-model="newRequest.estimated_participants" type="number" min="0" step="1">
				</LabeledObject>
				<Switch v-model="newRequest.online" :label="trans('events.requests.online')"/>
			</div>
			<LabeledObject>
				<template #label>{{ trans('events.requests.observations') }}</template>
				<textarea v-model="newRequest.observations" maxlength="65535"/>
			</LabeledObject>
		</template>
		<p v-else-if="periods.length === 0" class="text-gray-400">{{ trans('admin.periods.exceptions.closed') }}</p>
	</AppLayout>
</template>
<script setup lang="ts">
import 'dayjs/locale/es'
import axios from 'axios'
import dayjs from 'dayjs'
import {route} from 'ziggy-js'
import {ref, type Ref} from 'vue'
import {router} from '@inertiajs/vue3'
import {trans} from 'laravel-vue-i18n'
import {Color} from '@/Types/Enums/Color'
import AppLayout from '@/Layouts/AppLayout.vue'
import Switch from '@/Components/Switch/Switch.vue'
import LabeledObject from '@/Components/LabeledObject.vue'
import PrimaryButton from '@/Components/Buttons/PrimaryButton.vue'
import SearchLocation from '@/Components/Search/SearchLocation.vue'
import SelectColleges from '@/Components/Search/SelectColleges.vue'
import {type EventPeriod} from '@/Types/Periods/Events/EventPeriod'
import {type EventRequest} from '@/Types/Periods/Events/EventRequest'
import {showInfo, showSuccess, showValidationErrors} from '@/helpers.ts'

const props = defineProps<{
	locale:string,
	hasPendingRequests:boolean,
	periods:Array<EventPeriod>,
}>()

dayjs.locale(props.locale)
const working:Ref<boolean> = ref(false)
const periods:Ref<Array<EventPeriod>> = ref(props.periods)
const newRequest:Ref<EventRequest> = ref({
	type: 'joseii',
	email: '',
	location: null,
	location_id: null,
	colleges: [],
	online: 0,
	lemma: '',
	begins_on: dayjs().format('YYYY-MM-DD'),
	ends_on: dayjs().format('YYYY-MM-DD'),
	estimated_participants: 0,
	observations: '',
})

function create():void{
	if(!working.value){
		working.value = true
		showInfo(trans('general.inserting'))
		axios.post(route('events.requests.store'), {
			type: newRequest.value.type,
			email: newRequest.value.email,
			location_id: newRequest.value.location_id,
			colleges: newRequest.value.colleges.map((college) => college.id),
			online: newRequest.value.online,
			name: newRequest.value.name,
			lemma: newRequest.value.lemma,
			begins_on: newRequest.value.begins_on,
			ends_on: newRequest.value.ends_on,
			estimated_participants: newRequest.value.estimated_participants,
			observations: newRequest.value.observations,
		}).then(() => {
			showSuccess(trans('general.inserted'))
			router.get(route('events.requests.index'))
		}).catch(showValidationErrors).finally(() => {
			working.value = false
		})
	}
}
</script>